<template>
  <div ref="containerRef"
       class="select"
       :class="[{
          'disabled': disabled
       }]"
  >
    <template v-if="request.select">
      <input type="hidden"
             v-for="value in formattedSelection"
             :key="value"
             :disabled="disabled"
             :name="`${SELECT_KEY}${parsedName}`"
             :value="value"
      />
    </template>
    <input
        v-for="value in inputValues"
        :key="value"
        type="hidden"
        :disabled="disabled"
        :name="parsedName"
        :value="value"
    />

    <SelectDropdown
        ref="selectRef"
        v-if="!inline"
        :input-values="temporaryValues"
        :input-class="inputClass"
        :small="small"
        :input-group-class="inputGroupClass"
        :disable-search="disableSearch"
        :disabled="disabled"
        :loading="loading"
        :preloading="preloading"
        :clearable="clearable"
        :multiple="multiple"
        :request="request"
        :placeholder="parsedPlaceholder"
        :container="containerRef"
        :options-props="selectOptionsProps"
        :all-selected="allSelected"
        :all-options="flatOptionsList"
        :title-key="titleKey"
        :value-key="valueKey"
        :initial-load="initialLoad"
        :is-static="isStatic"
        :confirmable="confirmable"
        :autocomplete="autocomplete"
        :quick-filter="quickFilter"
        :placement="placement"
        :error-message="requestError"
        @clear="clearValues"
        @undo="undo"
        @all="selectAll"
        @apply="apply"
        @close="onClose"
        @load="load"
        @remove="onRemove"
        @confirm="emit('confirm')"
        @focus="emit('focus')"
        @blur="emit('blur')"
    >
      <template #option="data">
        <slot name="option" v-bind="data" />
      </template>
      <template #emptyList="data">
        <slot name="emptyList" v-bind="data" />
      </template>
      <template #search="data">
        <slot name="search" v-bind="data" />
      </template>
      <template #footer>
        <slot name="footer" />
      </template>
    </SelectDropdown>

    <InlineSelect
        v-else
        ref="selectRef"
        :disable-search="disableSearch"
        :disable-controls="disableControls"
        :request="request"
        :multiple="multiple"
        :is-static="isStatic"
        :options-props="selectOptionsProps"
        @all="selectAll"
        @apply="apply"
    >
      <template #option="data">
        <slot name="option" v-bind="data" />
      </template>
      <template #emptyList="data">
        <slot name="emptyList" v-bind="data" />
      </template>
    </InlineSelect>
  </div>
</template>
<script setup>
import FormControlProps from "@/js/Elements/Form/FormControlProps.js";
import SelectDropdown from "@/js/Elements/Select/SelectDropdown.vue";
import {computed, inject, nextTick, reactive, ref, watch} from "vue";
import {useFormControlParser} from "@/js/Elements/Form/FormControl.js";
import {useI18n} from "vue-i18n";
import InlineSelect from "@/js/Elements/Select/InlineSelect.vue";
import {useStore} from "vuex";
import {escapeRegex} from "@/js/helpers.js";

const PRELOAD_KEY = 'preload';
const SELECT_KEY = 'selected_';
const SELECT_GROUP_KEY = 'group_id';
const SELECT_GROUP_WITH_SEARCH_KEY = 's_group_id';
const SELECT_ITEM_KEY = 'id';
const SELECT_SEARCH_KEY = 's';

defineExpose({
  reload: freshLoad,
  focus: () => {
    if (!selectRef.value) return;
    selectRef.value.open();
  },
  clear: clearValues.bind(null, true),
})

const { t } = useI18n();
const store = useStore();
const formData = inject('form:data', {});
const emit = defineEmits(['update:modelValue', 'confirm', 'focus', 'blur', 'confirm', 'close', 'select']);
const props = defineProps({
  ...FormControlProps,
  inputClass: [String, Object, Array],
  optionsClass: [String, Object, Array],
  showGoToPage: Boolean,
  showPerPage: Boolean,
  quickFilter: Boolean,
  searchFilter: {
    type: Function,
    default: (option, search, key) => {
      if (!search) return true;
      search = new RegExp(escapeRegex(search.toString().toLowerCase()));
      return option[key].toString().toLowerCase().match(search);
    }
  }
});

const {parsedPlaceholder, parsedName, parsedLabel } = useFormControlParser(props, {
  defaultPlaceholder: t('validation.select_placeholder'),
});
const selectFieldName = computed(() => parsedName.value.replace('[]', ''));

// Variables
const containerRef = ref();
const selectRef = ref();
const isGrouped = ref(false);
const loading = ref(false);
const options = reactive([]);
const inputValues = reactive([]);
const temporaryValues = reactive([]);
const request = reactive({
  url: typeof props.payload === 'string' ? props.payload : null,
  search: null,
  page: 1,
  nextPage: 1,
  perPage: null,
  showPerPage: [],
  paginated: false,
  select: false,
  preSelection: null,
  selection: {}
});
const watchers = {};
const allSelected = ref(0);
const preloadedOptions = reactive([]);
const initialLoad = ref(false);
const isStatic = ref(false);
const preloading = ref(false);
const requestError = ref();

let ignoreValueWatcher = false;
let timer = null;

// Computed variables
const selectOptionsProps = computed(() => {
  const properties = Object.assign(JSON.parse(JSON.stringify(props)), {
    options,
    request,
    loading: loading.value,
    multiple: props.multiple,
    isGrouped: isGrouped.value,
    initialLoad: initialLoad.value,
    showPagination: initialLoad.value && !isStatic.value && ((props.inline || (!props.inline && isGrouped.value))),
    onSelect,
    onSelectGroup,
    onGroupToggle(group) {
      group = options.find(g => g[props.groupValueKey] === group[props.groupValueKey]);
      group.expanded = group.expanded ? 0 : 1;
    },
    onScrollEnd() {
      if ((!props.inline && isGrouped.value) || props.autocomplete) return;
      if (request.page === request.lastPage) return;
      load();
    },
    onPageChange(value) {
      initialLoad.value = false;
      options.splice(0);
      request.nextPage = value;
      load();
    },

    onPerPage(value) {
      // initialLoad.value = false;
      // options.splice(0);
      console.log('onPerPageChange', value);
      // load();
    }
  });

  return properties;
});
const flatOptionsList = computed(() => {
  let list = options;
  if(options.length && typeof options[0]?.items !== 'undefined') {
    list = options.map(option => option.items).flat();
  }
  return [...list, ...preloadedOptions];
});

const formattedSelection = computed(() => {
  return Object.values(request.selection).map((value) => {
    return [value.key, value.selected === 1, value.value === null ? '' : value.value].join(';')
        + (value.additional ? `|${value.additional}` : '');
  });
});

const shouldPreload = computed(() => {
  if (request.paginated && !temporaryValues.length) return false;
  return !props.inline && (request.select || request.paginated);
});


// Initiation
init();

function init() {
  applyModelValue();
  storeValues(true);

  if (props.autocomplete) {
    setupWatchers();
  } else if (typeof props.payload !== 'string') {
    isStatic.value = true;
    initialLoad.value = true;
    addOptions(props.payload);
  } else {
    parsePayload();

    if (shouldPreload.value) {
      preload();
    } else {
      preloading.value = true;
      load();
    }
  }
}

function parsePayload() {
  if (props.payload.match(/(payload\/paginated\/)/)) {
    request.paginated = true;
  }

  if (props.payload.match(/(payload\/select\/)/)) {
    request.select = true;
  }
}

// Functions
function load(force = false) {
  if (requestError.value) return;

  if (!force && (loading.value || isStatic.value || (request.lastPage !== null && request.page > request.lastPage))) {
    return;
  }
  loading.value = true;

  unwatch(force);

  const params = props.params || {};
  params[props.searchKey] = request.search;
  params.page = request.nextPage;
  params.per_page = request.perPage;

  if (request.select) {
    params[`${SELECT_KEY}${selectFieldName.value}`] = formattedSelection.value;
  }

  return sendRequest(params).then(({ data, pagination }) => {
    if (pagination) {
      request.paginated = true;
      request.page = pagination.current_page;
      request.url = pagination.next_page_url;
      request.lastPage = pagination.last_page;
      request.nextPage = request.page + 1;
      request.perPage = pagination.per_page;
      request.showPerPage = pagination?.show_per_page || [];

      if (request.nextPage >= pagination.last_page) {
        request.nextPage = pagination.last_page;
      }
    } else if (!request.select) {
      isStatic.value = !props.autocomplete;
    }

    addOptions(data);
    initialLoad.value = true;
  }).finally(() => {
    if (preloading.value) {
      preloading.value = false;
    }
    loading.value = false;
  });
}

function freshLoad(force = false) {
  // Clear parameters for fresh load
  initialLoad.value = false;
  loading.value = false;
  request.lastPage = null;
  request.page = 1;
  request.nextPage = 1;
  options.splice(0);
  load(force);
}

function preload() {
  if (preloading.value) return;
  preloading.value = true;

  const params = {};

  if (request.select) {
    if (!request.preSelection) {
      temporaryValues.forEach(value => {
        addToSelection(value, true, SELECT_ITEM_KEY);
      });
    }

    params[`${SELECT_KEY}${selectFieldName.value}`] = formattedSelection.value;
    params[PRELOAD_KEY] = 1;
  } else if (request.paginated) {
    params[PRELOAD_KEY] = temporaryValues;
  }

  return sendRequest(params).then(({ data, preSelection }) => {
    request.preSelection = preSelection;
    (request.select ? preSelection.items : data).forEach((option) => {
      preloadedOptions.push(option);
    });

    if (request.select) {
      if (preSelection.items && preSelection.items.length) {
        preSelection.items.forEach(item => {
          addToValues(item[props.valueKey], true);
        });

        if (!initialLoad.value) {
          emitSelect();
        }
      } else {
        clearValues();
      }
    }
  }).finally(() => {
    preloading.value = false;
  });
}

function sendRequest(params = {}) {
  return new Promise((resolve, reject) => {
    if (requestError.value) {
      return reject();
    }

    store.dispatch('payload/load', {
      url: props.payload,
      params: Object.assign(props.params || {}, params),
      cache: false,
      fullResponse: true
    }).then((response) => {
      let data = response?.data?.data?.data || response?.data?.data;
      let pagination = response?.data?.data;
      const preSelection = response?.data?.data?.selection;

      if (Object.prototype.toString.call(data) === "[object Object]") {
        data = Object.values(data);
      }

      if (Object.prototype.toString.call(pagination) !== "[object Object]") {
        pagination = null;
      }

      resolve({
        pagination: pagination,
        data: data && Array.isArray(data) ? data : [],
        preSelection,
      });
    }).catch((error) => {
      console.log(error);
      if (error?.response?.data?.message) {
        requestError.value = error.response.data.message;
      } else {
        requestError.value = error?.message;
      }
      reject(error);
    });
  });

}


function setOptionVisibility(option) {
  if (typeof option.items !== 'undefined') {
    option.items = option.items.map(setOptionVisibility);
    option._visible_ = !!option.items.filter(option => option._visible_).length;
  } else {
    option._visible_ = !!(request.search && isStatic.value ? props.searchFilter(option, request.search, props.titleKey) : true);
  }

  return option;
}

function addOptions(list) {
  list = JSON.parse(JSON.stringify(list || []));
  (Array.isArray(list) ? list : Object.values(list)).map(option => {
    option = mapOption(option, typeof option?.items !== 'undefined');
    if (typeof option?.items !== 'undefined') {
      isGrouped.value = true;
      option.expanded = true;
      option.items = option.items.map(i => mapOption(i));
    }

    options.push(option);
  });

  // Update model value after each options refresh
  if (request.select) {
    parseAllSelectedFromSelection();
    storeValues(true);
  } else if (isStatic.value) {
    clearStaticPayloadValues();
  }

  if (!props.inline || isStatic.value) {
    setupWatchers();
    return;
  }

  nextTick(setupWatchers);
}

function mapOption(option, isGroup = false) {
  if (request.select || typeof option._selected !== 'undefined') {
    option.selected = typeof option.selected === 'undefined' ? option._selected : option.selected;
    if (!option.selected) {
      allSelected.value = 0;
    }

    if (!isGroup) {
      addToValues(option[props.valueKey].toString(), option.selected);
    }
  }
  return setOptionVisibility(Object.assign(option, {
    selected: option.selected ? 1 : 0
  }));
}

function debouncedLoad() {
  clearTimeout(timer);
  timer = setTimeout(freshLoad, 300);
}

function setupWatchers() {
  if (!watchers.modelValue) {
    watchers.search = watch(() => request.search, (newValue, oldValue) => {
      request.search = newValue;
      if (!isStatic.value) {
        return debouncedLoad();
      }
      options.map(setOptionVisibility);
    });
  }

  if (!watchers.modelValue) {
    watchers.modelValue = watch(() => props.modelValue, (value) => {
      if (ignoreValueWatcher) return;

      ignoreValueWatcher = true;
      applyModelValue();
      storeValues(true, false);
      nextTick(() => {
        updateModelValue();
        nextTick(() => {
          ignoreValueWatcher = false;
        })
      })
    }, {deep: props.multiple, immediate: true});
  }

  if (!watchers.payload) {
    watchers.payload = watch(() => props.payload, (value) => {
      if (typeof props.payload === 'string') {
        freshLoad(true);
        return;
      }
      unwatch();
      options.splice(0);
      addOptions(value);
    }, { deep: typeof props.payload === 'string' });
  }

  if (!watchers.request) {
    watchers.request = watch(() => request.perPage, (n, o) => {
      if (n == o) return;
      freshLoad(true);
    })
  }
}

function unwatch(leavePayloadWatcher = false) {
  Object.keys(watchers).forEach(key => {
    if (key === 'payload' && leavePayloadWatcher) return;
    watchers[key]();
    delete watchers[key];
  });
}

function parseAllSelectedFromSelection() {
  if (!request.select) return;
  let selected = !Object.values(request.selection).find(s => !s.selected);
  if (selected) {
    selected = !!Object.values(request.selection).find(s => !s.value || s.value === request.search);
  }
  allSelected.value = selected ? 1 : 0;
}

function onSelect(option) {
  if (option.selected && !props.multiple && !props.clearable) {
    return;
  }

  const selected = option.selected ? 0 : 1;
  const value = option[props.valueKey];

  option.selected = selected;


  if (request.paginated) {
    const index = preloadedOptions.findIndex(o => o[props.valueKey].toString() === option[props.valueKey].toString());
    if (selected && index === -1) {
      preloadedOptions.push(option);
    }
  }

  addToSelection(option, selected, SELECT_ITEM_KEY);
  addToValues(value, selected);
  storeValues();

  parseAllSelectedFromSelection();

  if (!props.multiple && selectRef.value?.close) {
    selectRef.value.close();
  }
}

function onRemove(value) {
  if (typeof value === 'undefined' || value === null) return;
  addToValues(value, 0);
  storeValues(true);
  selectOptions();

  const index = preloadedOptions.findIndex(o => o[props.valueKey].toString() === value);
  if (index === -1) return;
  preloadedOptions.splice(index, 1);
}

function onSelectGroup(group) {
  group.items.forEach(option => {
    addToValues(option[props.valueKey], group.selected);
    option.selected = group.selected;
    delete request.selection[`${option[props.valueKey]}-${SELECT_ITEM_KEY}`];
  });

  addToSelection(group, group.selected, request.search ? SELECT_GROUP_WITH_SEARCH_KEY : SELECT_GROUP_KEY);
  storeValues();
}

function selectAll(selected) {
  if (request.select) {
    if (!request.search) {
      Object.keys(request.selection).forEach(key => delete request.selection[key]);
    }

    request.selection[`${request.search}-${SELECT_SEARCH_KEY}`] = {
      key: SELECT_SEARCH_KEY,
      value: request.search,
      search_key: props.titleKey,
      selected
    };
  }

  allSelected.value = selected;
  flatOptionsList.value.forEach(option => {
    addToValues(option[props.valueKey].toString(), selected);
  });
  selectOptions();
  storeValues();
}

function undo() {
  allSelected.value = 0;
  temporaryValues.splice(0);
  selectOptions(inputValues);

  preloadedOptions.forEach((option, index) => {
    const value = inputValues.find(v => v === option[props.valueKey].toString());
    if (!value) {
      preloadedOptions.splice(0, index);
    }
  });
}

function clearValues(force = false) {
  allSelected.value = 0;
  temporaryValues.splice(0);
  selectOptions();
  Object.keys(request.selection).forEach(key => {
    delete request.selection[key];
  });
  storeValues(force);
}

function addToSelection(option, selected, key) {
  if (!request.select) return;

  let value = null;
  let groupId = null;
  let additional = null;

  if (Object.prototype.toString.call(option) !== "[object Object]") {
    const object = {};
    object[props.valueKey] = option;
    object[props.groupKey] = null;
    option = object;
  }

  switch (key) {
    case SELECT_ITEM_KEY:
      value = option[props.valueKey];
      groupId = option[props.groupKey];
      break;

    case SELECT_GROUP_KEY:
    case SELECT_GROUP_WITH_SEARCH_KEY:
      value = option[props.valueKey];
      break;
  }

  const selectionKey = `${value}-${key}`;

  if (key === SELECT_ITEM_KEY) {
    if (!props.multiple) { // clear selection
      Object.keys(request.selection).forEach((k) => {
        if (k === selectionKey && selected) return;
        delete request.selection[k];
      });
    }
  }

  if (key === SELECT_GROUP_WITH_SEARCH_KEY) {
    additional = request.search;
  }

  request.selection[selectionKey] = {
    key,
    value,
    selected: selected ? 1 : 0,
    additional,
    groupId
  };
}

function addToValues(value, selected) {
  value = value.toString();

  if (!props.multiple) {
    temporaryValues.splice(0);
  }

  const index = temporaryValues.indexOf(value);
  if (selected) {
    if (index === -1) {
      temporaryValues.push(value);
    }
  } else {
    if (index !== -1) {
      temporaryValues.splice(index, 1);
    }
  }

  if (request.select) return;

  allSelected.value = flatOptionsList.value.length === temporaryValues.length ? 1 : 0;
}

function selectOptions(list) {
  flatOptionsList.value.forEach(option =>
      option.selected = (list || temporaryValues).indexOf(option[props.valueKey].toString()) !== -1 ? 1 : 0
  );
}

function onClose() {

}

function apply(values) {
  if (request.select) {
    if (!props.inline) {
      preload();
    }
    emitSelect();
    return;
  }

  applyChanges(values);
  storeValues(true);
  emitSelect();

  if (props.autocomplete) {
    unwatch();
    request.search = null;
    setupWatchers();
  }
}

// Remove select from options where not exists in options list
// Form data can provide invalid value
function clearStaticPayloadValues() {
  const values = temporaryValues.filter((value) => {
    return flatOptionsList.value.find(opt => opt[props.valueKey].toString() === value.toString());
  });
  applyChanges(values);
  storeValues(true);
}

function applyModelValue() {
  let values = (Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue])
      .filter(value => typeof value !== 'undefined' && value !== null && value !== '')
      .map(value => value.toString());

  if (values.length && !props.multiple) {
    values.splice(1);
  }

  applyChanges(values, true);
}

function applyChanges(values, unselectOldValue = false) {
  if (!Array.isArray(values)) return;

  if (!request.paginated || unselectOldValue) {
    temporaryValues.filter(value => values.indexOf(value) === -1)
        .forEach(value => addToValues(value, false)); // unselect old values
  }

  values.forEach(value => {
    addToValues(value, true);
  }); // select new values

  selectOptions();
}

function storeValues(force = false, updateModelVal = true) {
  if (!props.inline && props.multiple && !force) return;

  inputValues.splice(0);
  temporaryValues.forEach((value) => {
    inputValues.push(value);
  });

  if (request.select && formData) {
    formData[`${SELECT_KEY}${parsedName.value}`] = formattedSelection.value;
  }

  if (!updateModelVal) return;
  updateModelValue();
}

function emitSelect() {
  if (request.select) {
    emit('select', {
      data: formattedSelection.value,
      name: `${SELECT_KEY}${selectFieldName.value}`,
      selection: request.selection
    });
    return;
  }

  const selectedOptions = [...inputValues].map((value) => {
    return flatOptionsList.value.find(opt => opt[props.valueKey].toString() === value.toString());
  }).filter(v => v);
  emit('select', props.multiple ? (selectedOptions.length ? selectedOptions : null) : (selectedOptions.length ? selectedOptions[0] : null));
}

function updateModelValue() {
  emit('update:modelValue', props.multiple ? (inputValues.length ? inputValues : null) : (inputValues.length ? inputValues[0] : null));
}
</script>

<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";

.select {
  width: 100%;
  display: flex;
  flex-direction: column;

  &:deep(> .input-group) {
    flex-wrap: nowrap;
  }

  &:not(.select--inline) {
    &.open input {
      padding-left: 0;
      outline: 0;
    }
  }

  :deep(.select__arrow) {
    font-size: toRem(10) !important;
  }

  :deep(.select__search) {
    padding-left: 0;
  }

  &--sm {
    [class^="icon-chevron-"],
    .icon-search {
      font-size: toRem(10);
    }

    .form-control-sm + .input-group-text {
      padding-left: 0;
    }
  }
}
</style>